import type { ReebeloStoreT } from '@lambda/reebelo';

// ---------------------------------------------------------------------------------------
//                                 Product Related Types
// ---------------------------------------------------------------------------------------

// Product Channel Enum
export enum ProductChannelEnum {
  ADVERTISING = 'advertising',
  INTERNAL = 'internal',
  ORDERS = 'orders',
  STOREFRONT = 'storefront',
}

export type ProductFiltersOptionT = { id: string; name: string };
export type ProductFiltersVariantOptionT = {
  id: string;
  slug?: string;
  name: string;
  values: { value: string; displayValue?: string | null }[];
};

// Product Filters Type
export type ProductFiltersT = {
  categories?: ProductFiltersOptionT[];
  brands?: ProductFiltersOptionT[];
  variants?: ProductFiltersVariantOptionT[];
  priceRange?: { min: number; max: number };
};

// Brand Type
export type BrandT = {
  id: string; // should start with 'brd-'
  name: string;
};

// Category Type
export type CategoryT = {
  id: string; // should start with 'cat-'
  pk?: string; // Deprecated, use id instead
  name: string;
  slug: string;
  description?: string;
  children?: CategoryT[]; // Recursive structure
};

// Image Type
export type ImageT = {
  url: string;
};

// Variant Type
type VariantT = {
  name: string;
  slug: string;
};

// Variant Value Type
export type VariantValueT = {
  sequence: number;
  value: string;
  displayValue: string | null;
  hexValue?: string | null;
  hidden?: boolean;
};

// Product Variant Type
export type ProductVariantT = {
  variant: VariantT;
  sequence: number;
  values: VariantValueT[];
  hidden?: boolean;
};

// Specification Type
type SpecificationT = {
  name: string;
};

// Product Specification Type
type ProductSpecificationT = {
  specification: SpecificationT;
  value: string;
  sequence: number;
};

// Product Feature Type
type ProductFeatureT = {
  name: string;
  value: string;
  sequence: number;
  image?: ImageT;
};

// Sku Variant Type
export type SkuVariantT = {
  variant: VariantT;
  value: VariantValueT;
};

export enum DeliveryType {
  STANDARD = 'standard',
  EXPRESS = 'express',
}

export type DeliveryEstimateT = {
  type: DeliveryType;
  minDate: string;
  maxDate: string;
  minDays?: number; // Deprecated: use minDate
  maxDays?: number; // Deprecated: use maxDate
  cutoffTime?: string;
  cutoffTimeZone?: string;
  cutoffDeadline?: string;
  deliveryMaxDays?: number;
  deliveryMinDays?: number;
  /** Single-value handling time in business days for the dispatch portion. */
  handlingTime?: number;
  destinationCountry?: string;
  destinationState?: string;
  destinationRegion?: string;
};

export enum PriceComponentName {
  BUYER_PROTECTION_FEE = 'buyer_protection_fee',
}

export type PriceComponentsT = {
  name: PriceComponentName;
  price: number;
};

export type PriceComponentsForPriceT = {
  price: number;
  components: PriceComponentsT[];
};

export type OfferT = {
  id: string;
  price: number;
  score: number;
  skuId: string;
  stock: number;
  store: ReebeloStoreT;
  vendorOfferId: string;
  vendorSku: string;
  deliveryEstimates: DeliveryEstimateT[];
  vendor: { id: string; name: string };
  location: { postalCode: string; country: string };
  sku?: SkuT;
  featured?: boolean;
  priceComponents?: PriceComponentsT[];
};

export type AvailableVariantT = {
  score: number;
  offerId: string;
  skuId: string;
  price: number;
  featured?: boolean;
  variants: SkuVariantT[];
  /** The GTIN and MPN the store lists this SKU under, `null` where it has none. */
  identifiers?: { gtin: string | null; mpn: string | null };
};

// Sku Type
export type SkuT = {
  id: string; // should start with 'sku-'
  slug: string;
  title: string;
  rsku?: string;
  rskuV2?: string;
  image?: ImageT;
  images?: ImageT[];
  variants: SkuVariantT[];
  offers: OfferT[];
  compareAtPrice?: number;
  product?: ProductT;
  /** Sum of the store's offer stock for this SKU; 0 when sold out. */
  stock?: number;
};

// Product Type
export type ProductT = {
  id: string; // should start with 'prd-'
  name: string;
  slug: string;
  title: string;
  description?: string;
  psku: string; // Must start with "P-"
  brand?: BrandT;
  category: CategoryT;
  image?: ImageT;
  images?: ImageT[];
  variants?: ProductVariantT[];
  specifications?: ProductSpecificationT[];
  features?: ProductFeatureT[];
  skus?: SkuT[];
  faq?: { question: string; answer: string }[];
};

export type CompetitorPriceT = {
  /** Null when out of stock. */
  price: number | null;
  /** The listed price, where `price` is after a promo code. Null when out of stock. */
  originalPrice?: number | null;
  /** That promo code and its percent off. */
  discountCode?: string | null;
  discountPercent?: number | null;
  skuId: string;
  /** Absent until MsCatalog2 ships it; callers fall back to the home store. */
  store?: string;
  sourceName: string;
  sourceVendorName: string;
  url: string;
  /** Absent from an MsCatalog2 that returns in-stock rows only. */
  inStock?: boolean;
  lastUpdatedAt?: string;
};

// ---------------------------------------------------------------------------------------
//                                 MsCatalog2 Types
// ---------------------------------------------------------------------------------------
export enum OfferSortBy {
  RELEASE_DATE = 'releaseDate',
  PRICE = 'price',
  SALES = 'sales',
}

export enum OfferSortByDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export enum OfferGroupBy {
  PRODUCT = 'product',
  SKU = 'sku',
}

// Product Type
// For CatalogV2 API Calls
export type SearchProductsParamsT = {
  store?: ReebeloStoreT; // Required
  sortBy?: OfferSortBy; // Required
  sortDirection?: OfferSortByDirection; // Required
  groupBy?: OfferGroupBy;

  limit?: number;
  offset?: number;
  includeFilters?: boolean;

  categoryIds?: string[];
  categoryPks?: string[];
  categories?: string[];

  excludeCategories?: string[];

  brandIds?: string[];
  brands?: string[];

  skuIds?: string[];

  pskus?: string[];
  name?: string;

  channel?: ProductChannelEnum;

  variants?: Record<string, string[]>;

  minPrice?: number;
  maxPrice?: number;

  releaseYear?: number;
  featured?: boolean;
};

// For URL Search Params
export type SearchParamsT = {
  sortBy?: OfferSortBy;
  sortDirection?: OfferSortByDirection;
  categoryIds?: string[];
  brandIds?: string[];
  variants?: Record<string, string[]>;
  minPrice?: number;
  maxPrice?: number;
};

export type GetSkusConfigT = {
  store?: ReebeloStoreT; // Required
  sortBy?: OfferSortBy; // Required
  sortDirection?: OfferSortByDirection; // Required

  limit?: number;
  offset?: number;
  includeFilters?: boolean;

  categoryIds?: string[];
  categoryPks?: string[];
  categories?: string[];

  excludeCategories?: string[];

  brandIds?: string[];
  brands?: string[];

  pskus?: string[];

  channel?: ProductChannelEnum;

  variants?: Record<string, string[]>;

  minPrice?: number;
  maxPrice?: number;

  releaseYear?: number;
  featured?: boolean;
};

export type CreateSkuDto = {
  store: ReebeloStoreT;
  variants: Record<string, string>;
  psku: string;
};

export enum SearchQuerySortT {
  RELEVANCE = 'relevance',
  PRICE_ASC = 'price_asc',
  PRICE_DESC = 'price_desc',
}

export type SearchQueryParamsT = {
  q: string;
  store?: ReebeloStoreT;
  sort?: SearchQuerySortT;
  limit?: number;
  offset?: number;
  categoryIds?: string[];
  brandIds?: string[];
  variants?: Record<string, string[]>;
  minPrice?: number;
  maxPrice?: number;
  includeFilters?: boolean;
  includeStock?: boolean;
};

export type SearchResultVariantT = {
  id: string;
  name: string;
  value: string;
  displayValue: string | null;
  hexValue: string | null;
};

export type SearchSkuResultItemT = {
  id: string;
  slug: string;
  product: { id: string; slug: string; name: string };
  title: string;
  image: ImageT | null;
  price: number;
  brand: { id: string; name: string } | null;
  category: { id: string; name: string } | null;
  variants: SearchResultVariantT[];
  tier: 1 | 2;
  relevanceScore: number;
  score: number;
  categoryRank: number | null;
  productRank: number | null;
  totalSales: number;
  /** Sum of the store's offer stock; only present when includeStock is sent. */
  stock?: number;
};

export type SearchSkusResponseT = {
  items: SearchSkuResultItemT[];
  total: number;
  filters: ProductFiltersT;
};

export type SearchProductResultItemT = {
  id: string;
  slug: string;
  name: string;
  title: string;
  image: ImageT | null;
  priceMin: number;
  priceMax: number;
  brand: { id: string; name: string } | null;
  category: { id: string; name: string } | null;
  tier: 1 | 2;
  relevanceScore: number;
  categoryRank: number | null;
  productRank: number | null;
  totalSales: number;
};

export type SearchProductsResponseT = {
  items: SearchProductResultItemT[];
  total: number;
  filters: ProductFiltersT;
};

export type AdminProductT = {
  id: string;
  name: string;
  brand: Pick<BrandT, 'name'> | null;
  category: Pick<CategoryT, 'name'> | null;
  image: ImageT | null;
};

// One row of the admin product listing. `image` is the best available thumbnail: the
// service falls back to the first gallery picture, so there is no images array to check.
export type AdminProductListItemT = {
  id: string;
  psku: string;
  name: string;
  brand: Pick<BrandT, 'id' | 'name'> | null;
  category: Pick<CategoryT, 'id' | 'name' | 'slug'> | null;
  image: ImageT | null;
};

export type AdminProductSearchResultT = {
  items: AdminProductListItemT[];
  total: number;
};

export type AdminProductSearchQueryT = {
  q?: string;
  categoryIds?: string[];
  brandIds?: string[];
  offset?: number;
  limit?: number;
  store?: string;
};

// `handle` becomes the slug and `psku` is generated from the name when omitted. Send
// `brandName` instead of `brandId` for a brand that does not exist yet.
export type AdminProductCreateT = {
  name: string;
  handle?: string;
  psku?: string;
  categoryId: string;
  brandId?: string;
  brandName?: string;
};

export type CollectionT = {
  id: string;
  createdAt: string;
  updatedAt: string;
  store: ReebeloStoreT;
  position: number;
  description: string;
  skuPositions: string[];
  subjectType: 'product' | 'category';
  product?: ProductT;
  category?: CategoryT;
  skus?: SkuT[];
  totalStock?: number;
};

export type CollectionCsvUploadStatusT = 'pending' | 'processed' | 'failed';

export type CollectionCsvUploadFailureT = {
  rowIndex: number;
  row: Record<string, string>;
  reason: string;
};

export type CollectionCsvUploadT = {
  id: string;
  status: CollectionCsvUploadStatusT;
  failures: CollectionCsvUploadFailureT[] | null;
  rowsProcessed: number | null;
  createdAt: string;
};

export type SkuExportJobStatusT =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed';

export type SkuExportJobT = {
  id: string;
  status: SkuExportJobStatusT;
  rowCount: number | null;
  errorMessage: string | null;
  downloadUrl?: string;
  completedAt: string | null;
  createdAt: string;
};

export type ProductExportJobT = {
  id: string;
  status: SkuExportJobStatusT;
  rowCount: number | null;
  errorMessage: string | null;
  downloadUrl?: string;
  completedAt: string | null;
  createdAt: string;
};

export type ProductCompatibilityUploadFailureT = {
  rowIndex: number;
  psku: string;
  reason: string;
};

export type ProductCompatibilityUploadT = {
  id: string;
  status: SkuExportJobStatusT;
  rowsProcessed: number | null;
  productsUpdated: number | null;
  linksAdded: number | null;
  linksRemoved: number | null;
  errorCount: number | null;
  failures: ProductCompatibilityUploadFailureT[] | null;
  errorMessage: string | null;
  createdAt: string;
};

export type SearchCategoriesResultT = {
  items: CategoryT[];
  total: number;
};

export type SearchBrandsResultT = {
  items: BrandT[];
  total: number;
};

// One value of a category's variant pairing. `value` is the key vendor feeds resolve
// SKUs against and is never edited; `displayValue` is the editable label.
export type CategoryVariantValueT = {
  id: string;
  value: string;
  displayValue?: string | null;
  hexValue?: string | null;
};

export type CategoryVariantValuesResultT = {
  items: CategoryVariantValueT[];
  total: number;
};

export type CategoryVariantT = {
  // The CategoryVariant uuid — what title-config keys its variant rules on.
  // Distinct from `variant.id`, which is the variant's reebeloId.
  categoryVariantId: string;
  // `id` is the variant's reebeloId — what the reorder endpoint matches on.
  variant: VariantT & { id: string };
  optional: boolean;
  hidden?: boolean;
  sequence?: number;
  // The pairing's real size; `values` is only the embedded preview.
  valueCount?: number;
  values: CategoryVariantValueT[];
};

// ---------------------------------------------------------------------------
// Title config Layered per category and scoped by stores/channels.
// ---------------------------------------------------------------------------

export type TitleVariantConfigT = {
  categoryVariantId: string;
  hidden?: boolean | null;
  sequence?: number | null;
  delimiter?: string | null;
  displayValueFormat?: string | null;
  hiddenValueIds?: string[] | null;
};

// A static, non-variant word in the title (e.g. "Unlocked"). Unlike a variant,
// a word has no backing entity — `wordId` is the client-generated id that
// links a word's rows across layers, the same role categoryVariantId plays
// for a variant.
export type TitleWordConfigT = {
  wordId: string;
  text?: string | null;
  hidden?: boolean | null;
  sequence?: number | null;
  delimiter?: string | null;
};

export type TitleConfigT = {
  id: string;
  categoryId: string;
  // Admin-facing label for an override group (not used in rendering).
  name?: string | null;
  stores: ReebeloStoreT[];
  channels: ProductChannelEnum[];
  precedence: number;
  titlePrefix?: string | null;
  titleSuffix?: string | null;
  showBrandInTitle?: boolean | null;
  showCategoryInTitle?: boolean | null;
  variantConfigs: TitleVariantConfigT[];
  wordConfigs: TitleWordConfigT[];
};

// A resolved field plus the id of the layer that supplied it ('fallback' when
// no layer set it). Returned by the effective endpoint.
export type FieldSourceT<T> = { value: T; source: string };

export type AnnotatedTitleConfigT = {
  titlePrefix: FieldSourceT<string>;
  titleSuffix: FieldSourceT<string>;
  showBrandInTitle: FieldSourceT<boolean>;
  showCategoryInTitle: FieldSourceT<boolean>;
  variants: {
    categoryVariantId: string;
    hidden: FieldSourceT<boolean>;
    sequence: FieldSourceT<number>;
    delimiter: FieldSourceT<string | null>;
    displayValueFormat: FieldSourceT<string | null>;
    hiddenValueIds: FieldSourceT<string[]>;
  }[];
  words: {
    wordId: string;
    text: FieldSourceT<string>;
    hidden: FieldSourceT<boolean>;
    sequence: FieldSourceT<number>;
    delimiter: FieldSourceT<string | null>;
  }[];
};

export type EffectiveTitleConfigResultT = {
  layerIds: string[];
  config: AnnotatedTitleConfigT;
};

// One category's pairings, from the bulk `GET /v1/admin/category-variants` read.
// `pk` is the legacy category pk; `categoryId` the MsCatalog2 reebeloId.
export type CategoryVariantsByCategoryT = {
  pk: string;
  categoryId: string;
  variants: CategoryVariantT[];
};

// Body of the bulk `PUT /v1/admin/categories/:id/variants` write. The full desired set
// of a category's variants + values; applied additively server-side.
export type SetCategoryVariantsT = {
  variants: {
    variantId?: string;
    name?: string;
    optional?: boolean;
    values?: string[];
  }[];
  order?: string[];
};

// A variant type as offered by the attach flow's library search. Separate from the
// inline `VariantT` used by product/sku shapes, which carries no id.
export type VariantLibraryItemT = {
  id: string; // reebeloId, starts with 'var-'
  name: string;
  slug: string;
  // Zero means safe to delete: no categories implies no products, and so no offers.
  categoryCount: number;
  // reebeloIds. Lets the bulk attach modal mark what a variant already has.
  attachedCategoryIds: string[];
};

export type SearchVariantLibraryResultT = {
  items: VariantLibraryItemT[];
  total: number;
};

// ---------------------------------------------------------------------------------
// Admin product editor. Mirrors AdminProductDTO, which differs from ProductT in two
// ways that matter: every child row carries its `id`, because the command endpoints
// target one row per call, and nothing is filtered by `hidden` — an editor has to see
// what the storefront suppresses.
//
// Hand-maintained: these do not derive from rdto, so a field added there does not
// appear here on its own.
// ---------------------------------------------------------------------------------

export type AdminProductVariantValueImageT = {
  id: string;
  imageUrl: string;
  sequence: number;
};

export type AdminProductVariantValueT = {
  id: string;
  categoryVariantValueId: string;
  sequence: number;
  // Untranslated, and the key incoming offers resolve SKUs against: surfaced but never
  // editable.
  value: string;
  categoryDisplayValue?: string;
  displayValue?: string;
  titleDisplayValue?: string;
  hexValue?: string;
  // Colour-only, like hexValue: absent on storage, condition and carrier values.
  images?: AdminProductVariantValueImageT[];
};

export type AdminProductVariantT = {
  id: string;
  categoryVariantId: string;
  variant: VariantT & { id: string };
  sequence: number;
  showInTitle: boolean;
  titleSequence: number;
  values: AdminProductVariantValueT[];
};

export type AdminProductSpecificationT = {
  id: string;
  specification: { name: string };
  value: string;
  sequence: number;
};

export type AdminProductFeatureT = {
  id: string;
  name: string;
  value: string;
  sequence: number;
  image?: ImageT;
};

export type AdminProductFaqT = {
  id: string;
  question: string;
  answer: string;
  sequence?: number;
};

export type AdminProductDetailT = {
  id: string;
  psku: string;
  name: string;
  slug: string;
  description?: string;
  showBrandInTitle: boolean;
  releaseDate?: string;
  category: CategoryT;
  brand?: BrandT;
  // The primary image.
  image?: ImageT;
  // Overrides the primary for the advertising channel only. Absent means no override.
  adsImage?: ImageT;
  // Every picture the product has, primary included. Derived by flattening each colour's
  // images, so the ordering carries no meaning.
  images?: ImageT[];
  variants?: AdminProductVariantT[];
  specifications?: AdminProductSpecificationT[];
  features?: AdminProductFeatureT[];
  faq?: AdminProductFaqT[];
};

/**
 * One entry in a product's edit history. `message` is rendered by MsCatalog2 at write
 * time and is never recomputed, so a row reads as it did when it was written.
 *
 * Only what the V2 editor wrote — the trail starts the day it shipped, and V1's own
 * history stays in the Elasticsearch index it was written to.
 */
export type AdminProductAuditEventT = {
  id: string;
  createdAt: string;
  actorEmail: string;
  type: string;
  message: string;
};

export type AdminProductAuditEventsT = {
  items: AdminProductAuditEventT[];
  total: number;
};

// Exactly one of create / update / delete / move per call — the endpoints reject more.
export type RelativeMoveT = { id: string; beforeId?: string };

export type AdminSpecificationsCommandT = {
  create?: { name: string; value: string };
  update?: { id: string; name?: string; value?: string };
  delete?: { id: string };
  move?: RelativeMoveT;
  /**
   * The whole list at once, in order — what a pasted spec sheet becomes. Rows left out
   * of it are removed, and the array index becomes the displayed order, so a paste also
   * reorders. Capped at 200 by the service.
   */
  replace?: { items: { name: string; value: string }[] };
};

export type AdminFeaturesCommandT = {
  create?: { name: string; value: string; imageUrl?: string };
  update?: {
    id: string;
    name?: string;
    value?: string;
    imageUrl?: string | null;
  };
  delete?: { id: string };
  move?: RelativeMoveT;
};

export type AdminFaqsCommandT = {
  create?: { question: string; answer: string };
  update?: { id: string; question?: string; answer?: string };
  delete?: { id: string };
  move?: RelativeMoveT;
};

/**
 * `create` attaches a variant to the product by its reebeloId, restoring a previously
 * removed attachment rather than duplicating it. Values are added separately.
 *
 * `delete` removes one variant from one product and takes its values and SKUs with it.
 * It is refused outright while any of those SKUs has a live offer; otherwise the first
 * call comes back 409 with the counts and a second call with `confirm` proceeds.
 */
export type AdminProductVariantsCommandT = {
  create?: { variantId: string };
  update?: { id: string; showInTitle?: boolean; titleSequence?: number };
  move?: RelativeMoveT;
  delete?: { id: string; confirm?: boolean };
};

/**
 * `delete` removes one option from one product and takes the SKUs built from it, and its
 * color images, with it. Refused outright while any of those SKUs has a live offer, and
 * on the last option of a mandatory variant; otherwise the first call comes back 409 with
 * the SKU count and a second call with `confirm` proceeds.
 */
export type AdminProductVariantValuesCommandT = {
  create?: { categoryVariantValueId: string };
  update?: {
    id: string;
    displayValue?: string | null;
    titleDisplayValue?: string | null;
    hexValue?: string | null;
  };
  move?: RelativeMoveT;
  delete?: { id: string; confirm?: boolean };
};

// Delete here needs no confirmation, unlike on values: nothing resolves a SKU through an
// image.
export type AdminValueImagesCommandT = {
  create?: { imageUrl: string };
  delete?: { id: string };
  move?: RelativeMoveT;
};

/**
 * A write to one of a product's child collections, discriminated by collection name. Keeping the
 * command beside its collection is what stops an FAQ command being sent to the
 * specifications collection now that one route serves all six — the gateway itself
 * cannot tell them apart, since it passes each command through as `yup.mixed`.
 *
 * The discriminant lives in the body rather than the path so there is exactly one
 * copy of it. A `collections/{collection}` path segment would read better in logs,
 * but it would also be a second source of truth the gateway has no way to reject a
 * mismatch against — this service has no convention for raising its own 4xx.
 */
export type AdminProductCollectionWriteT =
  | { collection: 'specifications'; command: AdminSpecificationsCommandT }
  | { collection: 'features'; command: AdminFeaturesCommandT }
  | { collection: 'faqs'; command: AdminFaqsCommandT }
  | { collection: 'variants'; command: AdminProductVariantsCommandT }
  | {
      collection: 'variant-values';
      variantId: string;
      command: AdminProductVariantValuesCommandT;
    }
  | {
      collection: 'value-images';
      variantId: string;
      valueId: string;
      command: AdminValueImagesCommandT;
    };

export type SkuIdentifierTypeT = 'gtin' | 'ean' | 'upc' | 'mpn' | 'asin';

/**
 * One configuration of a product: the SKUs that are the same manufactured item in
 * different conditions, grouped by every variant value except condition and battery
 * health. That is the grain an identifier names — a GTIN identifies the product, not the
 * condition it arrived in — so it is the grain the editor works at, while storage stays
 * per-SKU.
 *
 * `skuId` is any member and addresses the whole group. An identifier's `skuCount` is how
 * many of the configuration's SKUs carry it: fewer than `skuCount` is a gap, and two
 * distinct values of one type is a conflict. Nothing backfills either, so surfacing them
 * is the point of the screen.
 *
 * An identifier's `store` is null when it applies in every store, and names the store
 * otherwise: a store's identifier overrides the all-stores one there. Gaps and conflicts
 * are judged on the all-stores identifiers alone, as MsCatalog2 judges completeness.
 */
export type ProductConfigurationT = {
  skuId: string;
  skuCount: number;
  variants: { slug: string; name: string; value: string }[];
  identifiers: {
    type: SkuIdentifierTypeT;
    value: string;
    store: ReebeloStoreT | null;
    skuCount: number;
  }[];
};

/**
 * `skuId` addresses a configuration rather than naming a row to change: the write reaches
 * every SKU sharing that SKU's base identity.
 *
 * `create` replaces, scoped to the type and store — a SKU carries at most one GTIN per
 * store afterwards, and setting one does not disturb the MPN or another store's GTIN.
 * There is no `update`; with replace semantics that is `create` with a different value.
 *
 * Without `store` the command reaches the all-stores identifier; with one, only that
 * store's override.
 */
type AdminIdentifierSectionT = {
  skuId: string;
  type: SkuIdentifierTypeT;
  value: string;
  store?: ReebeloStoreT;
};

export type AdminIdentifiersCommandT = {
  create?: AdminIdentifierSectionT;
  delete?: AdminIdentifierSectionT;
};

export type AdminIdentifiersResultT = {
  skuId: string;
  identifiers: { type: SkuIdentifierTypeT; value: string }[];
};

/**
 * One "Compare Prices" row: a list/compare-at price for one storage value in one store,
 * scoped to the product. `price` is cents, the same convention as `Offer.price`.
 *
 * `storage` addresses every SKU of the product sharing that storage value, across every
 * color and other variant — not one SKU. Setting or clearing a row reaches all of them.
 */
export type ComparePriceRowT = {
  storage: string;
  store: ReebeloStoreT;
  price: number;
};

/**
 * One product this product is compatible with. The link is symmetric and stored as two
 * mirrored rows on the service, so linking or unlinking from either side reaches both —
 * nothing here is one-directional. `id` is the compatible product's reebeloId.
 */
export type CompatibleProductT = {
  id: string;
  psku: string;
  name: string;
  slug: string;
};

// Exactly one of upsert / delete per call. There is no separate update — an upsert with
// the same (storage, store) as an existing row replaces its price.
export type AdminComparePricesCommandT =
  | {
      upsert: { storage: string; store: ReebeloStoreT; price: number };
      delete?: never;
    }
  | {
      upsert?: never;
      delete: { storage: string; store: ReebeloStoreT };
    };

export type AdminProductUpdateT = {
  name?: string;
  slug?: string;
  description?: string | null;
  brandId?: string | null;
  categoryId?: string;
  showBrandInTitle?: boolean;
  releaseDate?: string | null;
  imageUrl?: string | null;
  adsImageUrl?: string | null;
};

// A condition/battery sibling of a SKU: same product, identical for every
// variant except condition and battery health. The queried SKU is included.
export type RelatedSkuT = {
  skuId: string;
  condition: string | null;
  // Ordering sequence for the condition value (worst -> best); higher is better.
  conditionSequence: number | null;
  batteryHealth: string | null;
};

export type SkuAllocationOfferT = {
  skuId: string;
  // Customer-facing price in cents: the offer's base price plus the fees
  // itemised in priceComponents (e.g. buyer protection fee).
  price: number;
  stock: number;
  priceComponents?: { name: string; price: number }[];
};

/** Aggregated review figures for one vendor, from /v2/reviews/aggregate. */
export type VendorReviewsAggregateT = {
  total: number;
  average: number;
  /** Share of reviews that are good, e.g. 98.53 for 98.53%. */
  goodReviewsPercent: number;
  /** Review count per star rating, keyed by the rating. */
  breakdown: Record<string, number>;
};

/** One vendor review, as served by /v2/reviews. */
export type VendorReviewT = {
  id: string;
  createdAt: string;
  updatedAt: string;
  /** Where the review came from, e.g. "trustpilot". */
  source: string;
  /** Id of the review in `source`; shared by every line of a multi-item order. */
  externalId: string;
  skuId: string;
  vendorId: string;
  orderId: string;
  orderLineId: string;
  customerName: string;
  title: string;
  text: string;
  /** Star rating, 1-5. */
  review: number;
  url?: string;
  /** Title and text came back in the store language, not as written. */
  isTranslated?: boolean;
};

export type VendorReviewsResponseT = {
  items: VendorReviewT[];
  total: number;
};

/* -------------------------------------------------------------------------- */
/*  Cross-region product copy                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The catalog is two databases, not one per store: APAC shares one, the Americas and
 * Europe share the other. A copy therefore has exactly one destination.
 */
export type CatalogRegionT = 'us' | 'apac';

export type CopyOutcomeT =
  | 'copy'
  | 'same'
  | 'skip-empty-source'
  | 'skip-missing-color'
  | 'skip-ambiguous-color';

export type CopyStepT =
  | {
      kind: 'scalar';
      label: string;
      from: string | null;
      to: string | null;
      outcome: CopyOutcomeT;
    }
  | {
      kind: 'specification';
      label: string;
      payload: { name: string; value: string };
      /** The row to overwrite. Absent when the target has nothing under this key. */
      targetId?: string;
      outcome: CopyOutcomeT;
    }
  | {
      kind: 'feature';
      label: string;
      payload: { name: string; value: string; imageUrl?: string };
      /** The row to overwrite. Absent when the target has nothing under this key. */
      targetId?: string;
      outcome: CopyOutcomeT;
    }
  | {
      kind: 'faq';
      label: string;
      payload: { question: string; answer: string };
      /** The row to overwrite. Absent when the target has nothing under this key. */
      targetId?: string;
      outcome: CopyOutcomeT;
    }
  | {
      kind: 'colorImages';
      label: string;
      imageUrls: string[];
      outcome: CopyOutcomeT;
    };

export type CopyPlanT = {
  psku: string;
  from: CatalogRegionT;
  to: CatalogRegionT;
  steps: CopyStepT[];
};

// ---------------------------------------------------------------------------------------
//                   RT-17725 Attribute-based Inventory Uploads
// ---------------------------------------------------------------------------------------
// A vendor uploads a file naming a product by Model + variant values (Storage,
// Color, Carrier, etc.) instead of a Reebelo SKU ID. A row that matches an
// existing SKU publishes immediately; a row that doesn't registers a proposed
// SKU for catalog-team review instead.

export type InventoryUploadStatusT =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed';

export type InventoryUploadT = {
  id: string;
  vendorId: string;
  store: ReebeloStoreT;
  fileName: string;
  originalFileName: string | null;
  status: InventoryUploadStatusT;
  uploadedCount: number;
  underReviewCount: number;
  failedCount: number;
  totalCount: number;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateInventoryUploadResultT = {
  uploadId: string;
  url: string;
};

export type SearchInventoryUploadsParamsT = {
  vendorId: string;
  limit?: number;
  offset?: number;
};

export type SearchInventoryUploadsResultT = {
  items: InventoryUploadT[];
  total: number;
};

export type InventoryUploadLineStatusT =
  | 'pending'
  | 'processing'
  | 'uploaded'
  | 'under_review'
  | 'failed';

export type InventoryUploadLineErrorT = {
  column: string;
  value?: string;
  reason: string;
};

export type InventoryUploadLineT = {
  id: string;
  inventoryUploadId: string;
  rowNumber: number;
  status: InventoryUploadLineStatusT;
  model: string | null;
  vendorSku: string | null;
  resolvedProductId: string | null;
  skuId: string | null;
  proposedSkuId: string | null;
  // A row processed before errorMessage became an array can still hold the old
  // single-object shape -- jsonb has no schema to migrate historical rows with.
  errorMessage: InventoryUploadLineErrorT[] | InventoryUploadLineErrorT | null;
  processedAt: string | null;
};

export type SearchInventoryUploadLinesParamsT = {
  limit?: number;
  offset?: number;
};

export type SearchInventoryUploadLinesResultT = {
  items: InventoryUploadLineT[];
  total: number;
};

export type CopyStepResultT = CopyStepT & { applied: boolean; error?: string };

// RT-17816 proposed SKUs: variant combinations sellers asked for through Bulk import
// that have no SKU yet. Mirrors MsCatalog2's admin proposed-skus DTOs.
export type ProposedSkuStatusT = 'pending' | 'approved' | 'rejected';

// The only reasons a reviewer can give; MsCatalog2 stores the label as free text, and
// sellers see it in their upload history.
export const PROPOSED_SKU_REJECTION_REASONS = [
  'Combination does not exist',
  'Invalid variant value',
  'Duplicate submission',
] as const;

export type ProposedSkuT = {
  id: string;
  reebeloId: string;
  status: ProposedSkuStatusT;
  productName: string;
  categoryName: string;
  /** In the product's variant order. */
  variantValues: { variant: string; value: string }[];
  /** Vendors that asked for it, earliest first. `id` is `vendor.id`. */
  vendors: { id: string; name: string; requestedAt: string }[];
  submittedAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  /** The SKU an approval created. */
  skuId?: string;
};

export type ProposedSkuSearchQueryT = {
  status?: ProposedSkuStatusT[];
  categoryId?: string;
  vendorId?: string;
  offset?: number;
  limit?: number;
};

export type ProposedSkuFilterOptionT = { id: string; name: string };

export type ProposedSkusPageT = {
  items: ProposedSkuT[];
  /** Total filtered items, ignoring paging. */
  total: number;
  /** Vendors and categories with a proposal in the requested statuses. */
  filters: {
    vendors: ProposedSkuFilterOptionT[];
    categories: ProposedSkuFilterOptionT[];
  };
};

/** One batch decision: a failure on one proposal does not stop the others. */
export type ProposedSkuDecisionResultT = {
  succeeded: string[];
  failed: { id: string; error: string }[];
};
